import { useEnhancedBrandStore } from '../../../stores/enhancedBrandStore'
import { useMemo } from 'react'
import { useDebounce } from '../../../hooks/useDebounce'
import { DualRangeSlider } from '../../ui/DualRangeSlider'

// State name to abbreviation mapping
const STATE_ABBREVIATIONS: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
}

export function AdvancedFiltersSection() {
  const {
    filters,
    allBrands,
    setStatesFilter,
    setFoundedYearRange,
    setConfidenceScoreRange,
    setIncludeUnknownFoundedYear
  } = useEnhancedBrandStore()

  // Debounced filter updates to prevent rapid state changes
  const debouncedSetFoundedYearRange = useDebounce(setFoundedYearRange, 150)
  const debouncedSetConfidenceScoreRange = useDebounce(setConfidenceScoreRange, 150)

  // Get only states that have brand data with counts
  const stateData = allBrands.reduce((acc, brand) => {
    if (brand.hq_state) {
      acc[brand.hq_state] = (acc[brand.hq_state] || 0) + 1
    }
    return acc
  }, {} as Record<string, number>)

  const availableStates = Object.entries(stateData)
    .sort(([a], [b]) => a.localeCompare(b)) // Sort alphabetically
    .map(([state]) => state)

  // Calculate dynamic ranges (memoized to prevent recalculation on every render)
  const { minYear, maxYear } = useMemo(() => {
    const foundedYears = allBrands
      .map(b => b.founded_year)
      .filter((year): year is number => typeof year === 'number')
    
    return {
      minYear: foundedYears.length > 0 ? Math.min(...foundedYears) : 1752,
      maxYear: foundedYears.length > 0 ? Math.max(...foundedYears) : 2025
    }
  }, [allBrands])

  const toggleState = (state: string) => {
    const current = filters.states
    const updated = current.includes(state)
      ? current.filter(s => s !== state)
      : [...current, state]
    setStatesFilter(updated)
  }

  return (
    <div className="space-y-6">
      {/* States Filter */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
          Geographic Filters ({filters.states.length} states selected)
        </h4>
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            💡 <strong>Tip:</strong> Use the map above for visual state selection, or click state codes below for quick access.
          </p>
          <div className="max-h-40 overflow-y-auto grid grid-cols-6 sm:grid-cols-8 lg:grid-cols-10 gap-2">
            {availableStates.map(state => {
              const stateCode = STATE_ABBREVIATIONS[state] || state.substring(0, 2).toUpperCase()
              const brandCount = stateData[state] || 0
              return (
                <button
                  key={state}
                  onClick={() => toggleState(state)}
                  title={`${state} - ${brandCount} brands`}
                  className={`h-10 text-xs font-medium rounded border transition-all flex flex-col items-center justify-center ${
                    filters.states.includes(state)
                      ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <div className="font-semibold">{stateCode}</div>
                  <div className="text-[10px] opacity-75">{brandCount}</div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Range Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Founded Year Range */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">
            Founded Year: {filters.foundedYearRange[0]} - {filters.foundedYearRange[1]}
          </label>
          <div className="px-3 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <DualRangeSlider
              min={minYear}
              max={maxYear}
              value={filters.foundedYearRange}
              onChange={debouncedSetFoundedYearRange}
              className="mb-2"
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{minYear}</span>
              <span>{maxYear}</span>
            </div>
            
            {/* Include Unknown Founded Years */}
            <div className="flex items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
              <input
                type="checkbox"
                id="includeUnknownFoundedYear"
                checked={filters.includeUnknownFoundedYear}
                onChange={(e) => setIncludeUnknownFoundedYear(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700"
              />
              <label 
                htmlFor="includeUnknownFoundedYear"
                className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                Include brands with unknown founded years
              </label>
            </div>
          </div>
        </div>

        {/* Confidence Score Range */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">
            Data Quality: {filters.confidenceScoreRange[0]}/10 - {filters.confidenceScoreRange[1]}/10
          </label>
          <div className="px-3 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <DualRangeSlider
              min={0}
              max={10}
              value={filters.confidenceScoreRange}
              onChange={debouncedSetConfidenceScoreRange}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0/10</span>
              <span>10/10</span>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Note */}
      <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-300">
          <strong>Note:</strong> These are specialized filters for power users. Most filtering can be done using the visual charts above.
        </p>
      </div>
    </div>
  )
}